/**
 * A machine's counters, as its card shows them. No fetching here: the card
 * reads them once and hands them to both parts.
 *
 * - `MaintenanceMeters` — grounds container and descale, in the card's main
 *   area, next to the state.
 * - `CountersDetails` — behind the Statistics button: drinks in all and by
 *   kind, the water filter, the other counts, and when the machine sent them.
 *
 * A count the provider does not report is left out, not shown as 0.
 */
import { useTranslation } from 'react-i18next';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { CoffeeMachineCountersDto } from '@forsys/shared';
import { Timestamp } from '../../components/Timestamp';

/** Drinks listed by name; the rest are counted in one line. */
const LISTED = 6;

function Meter({ label, percent }: { label: string; percent: number }) {
  return (
    <div>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {percent} %
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={percent >= 80 ? 'warning' : 'primary'}
        aria-label={label}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </div>
  );
}

/** Grounds container and descale. Nothing when the machine reports neither. */
export function MaintenanceMeters({ counters }: { counters: CoffeeMachineCountersDto }) {
  const { t } = useTranslation('coffee');
  if (counters.groundsContainerPercent === null && counters.descalePercent === null) return null;
  return (
    <Stack spacing={1}>
      {counters.groundsContainerPercent !== null && (
        <Meter label={t('counters.grounds')} percent={counters.groundsContainerPercent} />
      )}
      {counters.descalePercent !== null && <Meter label={t('counters.descale')} percent={counters.descalePercent} />}
    </Stack>
  );
}

export function CountersDetails({ counters }: { counters: CoffeeMachineCountersDto }) {
  const { t } = useTranslation('coffee');
  const hasFilter = (counters.waterFilterCount ?? 0) > 0 || (counters.waterFilterPercent ?? 0) > 0;
  const facts = [
    counters.cancelledDrinks !== null && t('counters.cancelled', { count: counters.cancelledDrinks }),
    counters.doubleDrinks !== null && t('counters.double', { count: counters.doubleDrinks }),
    counters.descaleCount !== null && t('counters.descaled', { count: counters.descaleCount }),
    hasFilter && counters.waterFilterCount !== null && t('counters.filters', { count: counters.waterFilterCount }),
  ].filter((fact): fact is string => typeof fact === 'string');

  if (counters.totalDrinks === null && counters.drinks.length === 0 && counters.groundsContainerPercent === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('counters.none')}
      </Typography>
    );
  }

  const listed = counters.drinks.slice(0, LISTED);
  const rest = counters.drinks.length - listed.length;

  return (
    <Stack spacing={1.5}>
      {counters.totalDrinks !== null && (
        <Typography variant="h6" component="p">
          {t('counters.total', { count: counters.totalDrinks })}
        </Typography>
      )}

      {listed.length > 0 && (
        <Stack spacing={0.25}>
          {listed.map(({ drink, count }) => (
            <Stack key={drink} direction="row" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="body2">{t(`drink.${drink}`)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {count}
              </Typography>
            </Stack>
          ))}
          {rest > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('counters.more', { count: rest })}
            </Typography>
          )}
        </Stack>
      )}

      {hasFilter && counters.waterFilterPercent !== null && (
        <Meter label={t('counters.filter')} percent={counters.waterFilterPercent} />
      )}

      {facts.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {facts.join(' · ')}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('counters.reported')}
        {counters.reportedAt && (
          <>
            {': '}
            <Timestamp value={counters.reportedAt} />
          </>
        )}
        . {t('counters.old')}
      </Typography>
    </Stack>
  );
}
